import React, { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Star, Check, X, Eye, AlertCircle, MessageSquare, Bell, Home, Upload, Trash2, Calendar, MapPin, FileText, Image as ImageIcon, Edit, Save, Building2, LogOut, BarChart3 } from 'lucide-react';
import { Switch } from '@/components/ui/switch';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { toast } from 'sonner';
import axios from 'axios';
import { AnalyticsView } from '@/components/analytics';

// Tell axios to send cookies with every request
axios.defaults.withCredentials = true;

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Predefined banner messages
const PREDEFINED_MESSAGES = [
  {
    id: 'construction',
    label: 'Under Construction',
    message: "Site Under Construction: We're currently updating our website. Some features and content may be incomplete, outdated, or inaccurate. Thank you for your patience as we improve your experience!"
  },
  {
    id: 'maintenance',
    label: 'Scheduled Maintenance',
    message: "Scheduled Maintenance: Our website is undergoing scheduled maintenance. Some features may be temporarily unavailable. We'll be back shortly!"
  },
  {
    id: 'new_features',
    label: 'New Features Coming',
    message: "Exciting Updates Coming Soon: We're adding new features to serve you better. Stay tuned for improvements to your browsing experience!"
  },
  {
    id: 'custom',
    label: 'Custom Message',
    message: ''
  }
];

export default function AdminTestimonials() {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [password, setPassword] = useState('');
  const [mainTab, setMainTab] = useState('testimonials'); // 'testimonials', 'banner', 'signups', 'openhouses', or 'settings'
  const [reviewTab, setReviewTab] = useState('pending');
  
  // Testimonial states
  const [pendingReviews, setPendingReviews] = useState([]);
  const [approvedReviews, setApprovedReviews] = useState([]);
  
  // Client Signups states
  const [clientSignups, setClientSignups] = useState([]);
  const [editingNotes, setEditingNotes] = useState({});
  
  // Banner states
  const [bannerSettings, setBannerSettings] = useState(null);
  const [selectedTemplate, setSelectedTemplate] = useState('construction');
  const [customMessage, setCustomMessage] = useState('');
  const [isBannerActive, setIsBannerActive] = useState(true);
  
  // Open House states
  const [openHouses, setOpenHouses] = useState([]);
  const [newOpenHouse, setNewOpenHouse] = useState({
    address: '',
    open_house_date: '',
    description: ''
  });
  const [selectedPhotos, setSelectedPhotos] = useState([]);
  const [selectedVideos, setSelectedVideos] = useState([]);
  const [selectedFlyer, setSelectedFlyer] = useState(null);
  const [selectedMLSListing, setSelectedMLSListing] = useState(null);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [editingOpenHouse, setEditingOpenHouse] = useState(null); // ID of open house being edited
  const [editFormData, setEditFormData] = useState({
    address: '',
    open_house_date: '',
    description: ''
  });
  
  // Agent Photo states
  const [agentPhoto, setAgentPhoto] = useState(null);
  const [agentPhotoUrl, setAgentPhotoUrl] = useState('');
  const [photoUploadProgress, setPhotoUploadProgress] = useState(0);
  
  // Property states
  const [properties, setProperties] = useState([]);
  const [newProperty, setNewProperty] = useState({
    city: '',
    builder: ''
  });
  const [selectedBrochure, setSelectedBrochure] = useState(null);
  const [propertyUploadProgress, setPropertyUploadProgress] = useState(0);
  
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    // Check if the user is already authenticated on page load
    const checkAuth = async () => {
      try {
        // This endpoint should be protected and will succeed if the cookie is valid
        await axios.get(`${API}/auth/status`); 
        setIsAuthenticated(true);
        fetchAllData();
      } catch (error) {
        setIsAuthenticated(false);
      }
    };
    checkAuth();
  }, []);

  const fetchAllData = () => {
    fetchReviews();
    fetchBannerSettings();
    fetchClientSignups();
    fetchOpenHouses();
    fetchAgentPhoto();
    fetchProperties();
  };

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      // The backend will handle the password check and set a secure httpOnly cookie.
      await axios.post(`${API}/login`, { password });
      setIsAuthenticated(true);
      toast.success('Login successful');
      fetchAllData();
    } catch (error) {
      console.error('Login failed:', error);
      toast.error(error.response?.data?.detail || 'Invalid password');
      setIsAuthenticated(false);
    }
  };

  const fetchClientSignups = async () => {
    try {
      const response = await axios.get(`${API}/signups`);
      setClientSignups(response.data);
    } catch (error) {
      console.error('Error fetching client signups:', error);
      toast.error('Failed to load client signups');
    }
  };

  const fetchReviews = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API}/testimonials/all`);
      const pending = response.data.filter((r) => r.status === 'pending');
      const approved = response.data.filter((r) => r.status === 'approved');
      setPendingReviews(pending);
      setApprovedReviews(approved);
    } catch (error) {
      console.error('Error fetching reviews:', error);
      toast.error('Failed to load reviews');
    } finally {
      setLoading(false);
    }
  };

  const fetchBannerSettings = async () => {
    try {
      const response = await axios.get(`${API}/banner-settings`);
      setBannerSettings(response.data);
      setIsBannerActive(response.data.is_active);
      
      const matchedTemplate = PREDEFINED_MESSAGES.find(
        (t) => t.message === response.data.message
      );
      
      if (matchedTemplate) {
        setSelectedTemplate(matchedTemplate.id);
      } else {
        setSelectedTemplate('custom');
        setCustomMessage(response.data.message);
      }
    } catch (error) {
      console.error('Error fetching banner settings:', error);
    }
  };

  const handleApprove = async (id) => {
    try {
      await axios.post(`${API}/testimonials/${id}/approve`);
      toast.success('Review approved');
      fetchReviews();
    } catch (error) {
      console.error('Error approving review:', error);
      toast.error('Failed to approve review');
    }
  };

  const handleReject = async (id) => {
    try {
      await axios.post(`${API}/testimonials/${id}/reject`);
      toast.success('Review rejected');
      fetchReviews();
    } catch (error) {
      console.error('Error rejecting review:', error);
      toast.error('Failed to reject review');
    }
  };

  const handleSaveBanner = async () => {
    setLoading(true);
    try {
      const selectedMsg = PREDEFINED_MESSAGES.find((t) => t.id === selectedTemplate);
      const message = selectedTemplate === 'custom' ? customMessage : selectedMsg.message;

      if (!message.trim()) {
        toast.error('Please enter a banner message');
        setLoading(false);
        return;
      }

      const response = await axios.post(`${API}/banner-settings`, {
        message,
        is_active: isBannerActive
      });

      setBannerSettings(response.data);
      toast.success('Banner settings updated! Changes will appear after page refresh.');
    } catch (error) {
      console.error('Error updating banner settings:', error);
      toast.error('Failed to update banner settings');
    } finally {
      setLoading(false);
    }
  };

  const handleUpdateNotes = async (signupId, notes) => {
    try {
      await axios.put(`${API}/signups/${signupId}/notes`, null, {
        params: { admin_notes: notes }
      });
      toast.success('Notes saved');
      fetchClientSignups();
    } catch (error) {
      console.error('Error updating notes:', error);
      toast.error('Failed to save notes');
    }
  };

  // Open House Management Functions
  const fetchOpenHouses = async () => {
    try {
      const response = await axios.get(`${API}/open-houses`);
      setOpenHouses(response.data);
    } catch (error) {
      console.error('Error fetching open houses:', error);
      toast.error('Failed to load open houses');
    }
  };

  const handleCreateOpenHouse = async (e) => {
    e.preventDefault();
    
    if (!newOpenHouse.address || !newOpenHouse.open_house_date) {
      toast.error('Address and date/time are required');
      return;
    }

    setLoading(true);
    setUploadProgress(10);

    try {
      const formData = new FormData();
      formData.append('address', newOpenHouse.address);
      formData.append('open_house_date', newOpenHouse.open_house_date);
      formData.append('description', newOpenHouse.description || '');

      if (selectedFlyer) {
        formData.append('flyer', selectedFlyer);
      }

      if (selectedMLSListing) {
        formData.append('mls_listing', selectedMLSListing);
      }

      // Append photos
      selectedPhotos.forEach((photo) => {
        formData.append('photos', photo);
      });

      // Append videos
      selectedVideos.forEach((video) => {
        formData.append('videos', video);
      });

      setUploadProgress(50);

      const response = await axios.post(`${API}/open-houses/upload`, formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
        onUploadProgress: (progressEvent) => {
          const percentCompleted = Math.round((progressEvent.loaded * 100) / progressEvent.total);
          setUploadProgress(50 + (percentCompleted * 0.5));
        },
      });

      setUploadProgress(100);
      toast.success('Open house created successfully!');
      
      // Reset form
      setNewOpenHouse({ address: '', open_house_date: '', description: '' });
      setSelectedPhotos([]);
      setSelectedVideos([]);
      setSelectedFlyer(null);
      setSelectedMLSListing(null);
      
      // Refresh list
      fetchOpenHouses();
    } catch (error) {
      console.error('Error creating open house:', error);
      toast.error(error.response?.data?.detail || 'Failed to create open house');
    } finally {
      setLoading(false);
      setTimeout(() => setUploadProgress(0), 1000);
    }
  };

  const handleDeleteOpenHouse = async (openHouseId) => {
    if (!confirm('Are you sure you want to delete this open house?')) {
      return;
    }

    try {
      await axios.delete(`${API}/open-houses/${openHouseId}`);
      toast.success('Open house deleted');
      fetchOpenHouses();
    } catch (error) {
      console.error('Error deleting open house:', error);
      toast.error('Failed to delete open house');
    }
  };


  const handleEditOpenHouse = (openHouse) => {
    setEditingOpenHouse(openHouse.id);
    setEditFormData({
      address: openHouse.address,
      open_house_date: openHouse.open_house_date,
      description: openHouse.description || ''
    });
    // Reset file selections for edit
    setSelectedPhotos([]);
    setSelectedVideos([]);
    setSelectedFlyer(null);
    setSelectedMLSListing(null);
  };

  const handleCancelEdit = () => {
    setEditingOpenHouse(null);
    setEditFormData({
      address: '',
      open_house_date: '',
      description: ''
    });
    setSelectedPhotos([]);
    setSelectedVideos([]);
    setSelectedFlyer(null);
    setSelectedMLSListing(null);
  };

  const handleUpdateOpenHouse = async (e) => {
    e.preventDefault();
    
    if (!editFormData.address || !editFormData.open_house_date) {
      toast.error('Address and date/time are required');
      return;
    }

    setLoading(true);
    setUploadProgress(10);

    try {
      const formData = new FormData();
      formData.append('address', editFormData.address);
      formData.append('open_house_date', editFormData.open_house_date);
      formData.append('description', editFormData.description || '');
      
      // Keep existing media unless new files are uploaded
      formData.append('keep_existing_media', 'true');

      if (selectedFlyer) {
        formData.append('flyer', selectedFlyer);
      }

      if (selectedMLSListing) {
        formData.append('mls_listing', selectedMLSListing);
      }

      // Append new photos
      selectedPhotos.forEach((photo) => {
        formData.append('photos', photo);
      });

      // Append new videos
      selectedVideos.forEach((video) => {
        formData.append('videos', video);
      });

      setUploadProgress(50);

      const response = await axios.put(`${API}/open-houses/${editingOpenHouse}`, formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
        onUploadProgress: (progressEvent) => {
          const percentCompleted = Math.round((progressEvent.loaded * 100) / progressEvent.total);
          setUploadProgress(50 + (percentCompleted * 0.5));
        },
      });

      setUploadProgress(100);
      toast.success('Open house updated successfully!');
      
      // Reset edit mode
      handleCancelEdit();
      
      // Refresh list
      fetchOpenHouses();
    } catch (error) {
      console.error('Error updating open house:', error);
      toast.error(error.response?.data?.detail || 'Failed to update open house');
    } finally {
      setLoading(false);
      setTimeout(() => setUploadProgress(0), 1000);
    }
  };


  // Agent Photo Functions
  const fetchAgentPhoto = async () => {
    try {
      const response = await axios.get(`${API}/agent-photo`);
      if (response.data.photo_url) {
        setAgentPhotoUrl(response.data.photo_url);
      }
    } catch (error) {
      console.error('Error fetching agent photo:', error);
    }
  };

  const handleAgentPhotoUpload = async (e) => {
    e.preventDefault();
    
    if (!agentPhoto) {
      toast.error('Please select a photo to upload');
      return;
    }

    setLoading(true);
    setPhotoUploadProgress(10);

    try {
      const formData = new FormData();
      formData.append('photo', agentPhoto);

      setPhotoUploadProgress(50);

      const response = await axios.post(`${API}/agent-photo/upload`, formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
        onUploadProgress: (progressEvent) => {
          const percentCompleted = Math.round((progressEvent.loaded * 100) / progressEvent.total);
          setPhotoUploadProgress(50 + (percentCompleted * 0.5));
        },
      });

      setPhotoUploadProgress(100);
      toast.success('Agent photo uploaded successfully!');
      
      setAgentPhotoUrl(response.data.photo_url);
      setAgentPhoto(null);
      
      // Reset file input
      const fileInput = document.getElementById('agent-photo-input');
      if (fileInput) fileInput.value = '';
      
    } catch (error) {
      console.error('Error uploading agent photo:', error);
      toast.error(error.response?.data?.detail || 'Failed to upload photo');
    } finally {
      setLoading(false);
      setTimeout(() => setPhotoUploadProgress(0), 1000);
    }
  };

  // Property Management Functions
  const fetchProperties = async () => {
    try {
      const response = await axios.get(`${API}/properties`);
      setProperties(response.data);
    } catch (error) {
      console.error('Error fetching properties:', error);
      toast.error('Failed to load properties');
    }
  };

  const handlePropertyUpload = async (e) => {
    e.preventDefault();
    
    if (!newProperty.city || !newProperty.builder) {
      toast.error('City and builder are required');
      return;
    }

    if (!selectedBrochure) {
      toast.error('Please select a brochure PDF file');
      return;
    }

    setLoading(true);
    setPropertyUploadProgress(10);

    try {
      const formData = new FormData();
      formData.append('city', newProperty.city);
      formData.append('builder', newProperty.builder);
      formData.append('brochure', selectedBrochure);

      setPropertyUploadProgress(50);

      await axios.post(`${API}/properties/upload`, formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
        onUploadProgress: (progressEvent) => {
          const percentCompleted = Math.round((progressEvent.loaded * 100) / progressEvent.total);
          setPropertyUploadProgress(50 + (percentCompleted * 0.5));
        },
      });

      setPropertyUploadProgress(100);
      toast.success('Property brochure uploaded successfully!');
      
      // Reset form
      setNewProperty({ city: '', builder: '' });
      setSelectedBrochure(null);
      const fileInput = document.getElementById('brochure-input');
      if (fileInput) fileInput.value = '';
      
      // Refresh properties list
      fetchProperties();
      
    } catch (error) {
      console.error('Error uploading property:', error);
      toast.error(error.response?.data?.detail || 'Failed to upload property');
    } finally {
      setLoading(false);
      setTimeout(() => setPropertyUploadProgress(0), 1000);
    }
  };

  const handleDeleteProperty = async (propertyId) => {
    if (!confirm('Are you sure you want to delete this property?')) {
      return;
    }

    try {
      await axios.delete(`${API}/properties/${propertyId}`);
      toast.success('Property deleted successfully');
      fetchProperties();
    } catch (error) {
      console.error('Error deleting property:', error);
      toast.error('Failed to delete property');
    }
  };


  const handleToggleOpenHouse = async (openHouseId) => {
    try {
      await axios.put(`${API}/open-houses/${openHouseId}/toggle`);
      toast.success('Open house visibility updated');
      fetchOpenHouses();
    } catch (error) {
      console.error('Error toggling open house:', error);
      toast.error('Failed to update visibility');
    }
  };

  const handleFileSelect = (e, type) => {
    const files = Array.from(e.target.files);
    
    if (type === 'photos') {
      setSelectedPhotos([...selectedPhotos, ...files]);
    } else if (type === 'videos') {
      setSelectedVideos([...selectedVideos, ...files]);
    } else if (type === 'flyer') {
      setSelectedFlyer(files[0]);
    } else if (type === 'mls') {
      setSelectedMLSListing(files[0]);
    }
  };

  const removeFile = (index, type) => {
    if (type === 'photos') {
      setSelectedPhotos(selectedPhotos.filter((_, i) => i !== index));
    } else if (type === 'videos') {
      setSelectedVideos(selectedVideos.filter((_, i) => i !== index));
    } else if (type === 'flyer') {
      setSelectedFlyer(null);
    } else if (type === 'mls') {
      setSelectedMLSListing(null);
    }
  };


  if (!isAuthenticated) {
    return (
      <div className="min-h-screen pt-20 bg-gradient-to-br from-slate-50 to-blue-50 flex items-center justify-center">
        <Card className="max-w-md w-full mx-4" data-testid="admin-login-form">
          <CardHeader>
            <CardTitle className="text-2xl font-bold text-[#1e3a8a]">
              Admin Login
            </CardTitle>
          </CardHeader>
          <CardContent> 
            <form onSubmit={handleLogin} className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="password">Password</Label>
                <Input
                  id="password"
                  data-testid="admin-password-input"
                  type="password" 
                  placeholder="Enter admin password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                />
              </div>
              <Button
                data-testid="admin-login-btn"
                type="submit"
                className="w-full bg-[#1e3a8a] hover:bg-[#1e3a8a]/90 text-white"
                disabled={loading}
              >
                {loading ? 'Logging in...' : 'Login'}
              </Button>
            </form>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen pt-20 bg-gradient-to-br from-slate-50 to-blue-50 py-12">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="mb-8 flex justify-between items-center">
          <div>
            <h1 className="text-3xl font-bold text-[#1e3a8a] mb-2" style={{ fontFamily: 'Playfair Display, serif' }}>
              Admin Panel
            </h1>
            <p className="text-slate-600">Manage testimonials and site banner</p>
          </div>
          <Button 
            onClick={async () => {
              try {
                await axios.post(`${API}/logout`);
                setIsAuthenticated(false);
                toast.success('Logged out successfully');
              } catch (error) {
                toast.error('Logout failed. Please try again.');
              }
            }} 
            variant="outline" 
            className="border-red-300 text-red-600 hover:bg-red-50" 
            data-testid="logout-btn">
            <LogOut className="mr-2 h-4 w-4" /> Logout
          </Button> 
        </div>

        {/* Main Tabs */}
        <div className="flex flex-wrap space-x-4 mb-6 border-b border-slate-200">
          <button
            data-testid="testimonials-main-tab"
            onClick={() => setMainTab('testimonials')}
            className={`px-6 py-3 font-medium transition-colors relative ${
              mainTab === 'testimonials'
                ? 'text-[#1e3a8a] border-b-2 border-[#1e3a8a]'
                : 'text-slate-600 hover:text-[#1e3a8a]'
            }`}
          >
            <div className="flex items-center space-x-2">
              <MessageSquare className="h-5 w-5" />
              <span>Testimonials</span>
              {pendingReviews.length > 0 && (
                <span className="bg-red-500 text-white text-xs rounded-full px-2 py-0.5">
                  {pendingReviews.length}
                </span>
              )}
            </div>
          </button>
          <button
            data-testid="banner-main-tab"
            onClick={() => setMainTab('banner')}
            className={`px-6 py-3 font-medium transition-colors relative ${
              mainTab === 'banner'
                ? 'text-[#1e3a8a] border-b-2 border-[#1e3a8a]'
                : 'text-slate-600 hover:text-[#1e3a8a]'
            }`}
          >
            <div className="flex items-center space-x-2">
              <Bell className="h-5 w-5" />
              <span>Banner Control</span>
              {bannerSettings?.is_active && (
                <span className="bg-green-500 text-white text-xs rounded-full px-2 py-0.5">
                  Active
                </span>
              )}
            </div>
          </button>
          <button
            data-testid="signups-main-tab"
            onClick={() => setMainTab('signups')}
            className={`px-6 py-3 font-medium transition-colors relative ${
              mainTab === 'signups'
                ? 'text-[#1e3a8a] border-b-2 border-[#1e3a8a]'
                : 'text-slate-600 hover:text-[#1e3a8a]'
            }`}
          >
            <div className="flex items-center space-x-2">
              <Star className="h-5 w-5" />
              <span>Client Leads</span>
              {clientSignups.length > 0 && (
                <span className="bg-blue-500 text-white text-xs rounded-full px-2 py-0.5">
                  {clientSignups.length}
                </span>
              )}
            </div>
          </button>
          <button
            data-testid="openhouses-main-tab"
            onClick={() => setMainTab('openhouses')}
            className={`px-6 py-3 font-medium transition-colors relative ${
              mainTab === 'openhouses'
                ? 'text-[#1e3a8a] border-b-2 border-[#1e3a8a]'
                : 'text-slate-600 hover:text-[#1e3a8a]'
            }`}
          >
            <div className="flex items-center space-x-2">
              <Home className="h-5 w-5" />
              <span>Open Houses</span>
              {openHouses.length > 0 && (
                <span className="bg-purple-500 text-white text-xs rounded-full px-2 py-0.5">
                  {openHouses.length}
                </span>
              )}
            </div>
          </button>
          <button
            data-testid="properties-main-tab"
            onClick={() => setMainTab('properties')}
            className={`px-6 py-3 font-medium transition-colors relative ${
              mainTab === 'properties'
                ? 'text-[#1e3a8a] border-b-2 border-[#1e3a8a]'
                : 'text-slate-600 hover:text-[#1e3a8a]'
            }`}
          >
            <div className="flex items-center space-x-2">
              <Building2 className="h-5 w-5" />
              <span>Properties</span>
              {properties.length > 0 && (
                <span className="bg-green-500 text-white text-xs rounded-full px-2 py-0.5">
                  {properties.length}
                </span>
              )}
            </div>
          </button>
          <button
            data-testid="analytics-main-tab"
            onClick={() => setMainTab('analytics')}
            className={`px-6 py-3 font-medium transition-colors relative ${
              mainTab === 'analytics'
                ? 'text-[#1e3a8a] border-b-2 border-[#1e3a8a]'
                : 'text-slate-600 hover:text-[#1e3a8a]'
            }`}
          >
            <div className="flex items-center space-x-2">
              <BarChart3 className="h-5 w-5" />
              <span>Analytics</span>
            </div>
          </button>
          <button
            data-testid="settings-main-tab"
            onClick={() => setMainTab('settings')}
            className={`px-6 py-3 font-medium transition-colors relative ${
              mainTab === 'settings'
                ? 'text-[#1e3a8a] border-b-2 border-[#1e3a8a]'
                : 'text-slate-600 hover:text-[#1e3a8a]'
            }`}
          >
            <div className="flex items-center space-x-2">
              <Upload className="h-5 w-5" />
              <span>Settings</span>
            </div>
          </button>
        </div>

        {/* Testimonials Tab Content */}
        {mainTab === 'testimonials' && (
          <div>
            <div className="flex space-x-4 mb-6">
              <button
                data-testid="pending-tab"
                onClick={() => setReviewTab('pending')}
                className={`px-6 py-2 rounded-lg font-medium transition-colors ${
                  reviewTab === 'pending'
                    ? 'bg-[#1e3a8a] text-white'
                    : 'bg-white text-slate-600 hover:bg-slate-100'
                }`}
              >
                Pending ({pendingReviews.length})
              </button>
              <button
                data-testid="approved-tab"
                onClick={() => setReviewTab('approved')}
                className={`px-6 py-2 rounded-lg font-medium transition-colors ${
                  reviewTab === 'approved'
                    ? 'bg-[#1e3a8a] text-white'
                    : 'bg-white text-slate-600 hover:bg-slate-100'
                }`}
              >
                Approved ({approvedReviews.length})
              </button>
            </div>

            {loading ? (
              <div className="text-center py-12">
                <p className="text-slate-600">Loading reviews...</p>
              </div>
            ) : (
              <div className="space-y-4">
                {reviewTab === 'pending' && pendingReviews.length === 0 && (
                  <Card>
                    <CardContent className="pt-12 pb-12 text-center">
                      <p className="text-slate-600">No pending reviews</p>
                    </CardContent>
                  </Card>
                )}

                {reviewTab === 'pending' &&
                  pendingReviews.map((review) => (
                    <Card key={review.id} data-testid="pending-review-card">
                      <CardContent className="pt-6">
                        <div className="flex flex-col md:flex-row justify-between">
                          <div className="flex-1">
                            <div className="flex items-center mb-2">
                              <h3 className="text-xl font-semibold text-[#1e3a8a]">
                                {review.client_name}
                              </h3>
                              <span className="ml-3 px-2 py-1 bg-yellow-100 text-yellow-800 text-xs rounded">
                                Pending
                              </span>
                            </div>
                            <p className="text-sm text-slate-500 mb-3">{review.location}</p>
                            <div className="flex mb-3">
                              {[...Array(review.rating)].map((_, i) => (
                                <Star key={i} className="h-5 w-5 fill-[#d4af37] text-[#d4af37]" />
                              ))}
                            </div>
                            <p className="text-slate-600 italic">"{review.content}"</p>
                          </div>
                          <div className="flex md:flex-col space-x-2 md:space-x-0 md:space-y-2 mt-4 md:mt-0 md:ml-4">
                            <Button
                              data-testid="approve-review-btn"
                              onClick={() => handleApprove(review.id)}
                              className="bg-green-600 hover:bg-green-700 text-white"
                            >
                              <Check className="h-4 w-4 mr-1" />
                              Approve
                            </Button>
                            <Button
                              data-testid="reject-review-btn"
                              onClick={() => handleReject(review.id)}
                              variant="outline"
                              className="border-red-600 text-red-600 hover:bg-red-50"
                            >
                              <X className="h-4 w-4 mr-1" />
                              Reject
                            </Button>
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                  ))}

                {reviewTab === 'approved' && approvedReviews.length === 0 && (
                  <Card>
                    <CardContent className="pt-12 pb-12 text-center">
                      <p className="text-slate-600">No approved reviews yet</p>
                    </CardContent>
                  </Card>
                )}

                {reviewTab === 'approved' &&
                  approvedReviews.map((review) => (
                    <Card key={review.id} data-testid="approved-review-card">
                      <CardContent className="pt-6">
                        <div className="flex items-start justify-between">
                          <div className="flex-1">
                            <div className="flex items-center mb-2">
                              <h3 className="text-xl font-semibold text-[#1e3a8a]">
                                {review.client_name}
                              </h3>
                              <span className="ml-3 px-2 py-1 bg-green-100 text-green-800 text-xs rounded">
                                Approved
                              </span>
                            </div>
                            <p className="text-sm text-slate-500 mb-3">{review.location}</p>
                            <div className="flex mb-3">
                              {[...Array(review.rating)].map((_, i) => (
                                <Star key={i} className="h-5 w-5 fill-[#d4af37] text-[#d4af37]" />
                              ))}
                            </div>
                            <p className="text-slate-600 italic">"{review.content}"</p>
                          </div>
                          <Eye className="h-5 w-5 text-green-600 ml-4" />
                        </div>
                      </CardContent>
                    </Card>
                  ))}
              </div>
            )}
          </div>
        )}

        {/* Banner Tab Content */}
        {mainTab === 'banner' && (
          <div>
            {/* Banner Preview */}
            {bannerSettings && isBannerActive && (
              <Card className="mb-8 border-amber-200 bg-amber-50">
                <CardHeader>
                  <CardTitle className="text-lg">Banner Preview</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="bg-gradient-to-r from-amber-500 to-orange-500 text-white py-3 px-4 rounded-lg">
                    <div className="flex items-center justify-center">
                      <AlertCircle className="h-5 w-5 mr-3 flex-shrink-0" />
                      <p className="text-sm text-center">
                        {selectedTemplate === 'custom' ? customMessage : 
                          PREDEFINED_MESSAGES.find((t) => t.id === selectedTemplate)?.message}
                      </p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Banner Settings */}
            <Card data-testid="banner-settings-card">
              <CardHeader>
                <CardTitle>Banner Settings</CardTitle>
              </CardHeader>
              <CardContent className="space-y-6">
                {/* Banner Status Toggle */}
                <div className="flex items-center justify-between p-4 bg-slate-50 rounded-lg">
                  <div>
                    <Label htmlFor="banner-active" className="text-base font-semibold">
                      Banner Status
                    </Label>
                    <p className="text-sm text-slate-600 mt-1">
                      {isBannerActive ? 'Banner is currently visible to all users' : 'Banner is currently hidden'}
                    </p>
                  </div>
                  <Switch
                    id="banner-active"
                    checked={isBannerActive}
                    onCheckedChange={setIsBannerActive}
                    data-testid="banner-active-toggle"
                  />
                </div>

                {/* Message Template Dropdown */}
                <div className="space-y-2">
                  <Label htmlFor="message-template">Message Template</Label>
                  <Select
                    value={selectedTemplate}
                    onValueChange={setSelectedTemplate}
                  >
                    <SelectTrigger data-testid="message-template-select">
                      <SelectValue placeholder="Select a template" />
                    </SelectTrigger>
                    <SelectContent>
                      {PREDEFINED_MESSAGES.map((template) => (
                        <SelectItem key={template.id} value={template.id}>
                          {template.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                {/* Custom Message Input or Selected Message */}
                {selectedTemplate === 'custom' ? (
                  <div className="space-y-2">
                    <Label htmlFor="custom-message">Custom Message</Label>
                    <Textarea
                      id="custom-message"
                      data-testid="custom-message-textarea"
                      placeholder="Enter your custom banner message..."
                      value={customMessage}
                      onChange={(e) => setCustomMessage(e.target.value)}
                      rows={4}
                    />
                  </div>
                ) : (
                  <div className="space-y-2">
                    <Label>Selected Message</Label>
                    <div className="p-4 bg-slate-50 rounded-lg border border-slate-200">
                      <p className="text-sm text-slate-700">
                        {PREDEFINED_MESSAGES.find((t) => t.id === selectedTemplate)?.message}
                      </p>
                    </div>
                  </div>
                )}

                {/* Save Button */}
                <Button
                  data-testid="save-banner-btn"
                  onClick={handleSaveBanner}
                  disabled={loading}
                  className="w-full bg-[#1e3a8a] hover:bg-[#1e3a8a]/90 text-white py-6 text-lg"
                >
                  {loading ? 'Saving...' : 'Save Banner Settings'}
                </Button>
              </CardContent>
            </Card>

            {/* Instructions */}
            <Card className="mt-8 border-blue-200 bg-blue-50">
              <CardContent className="pt-6">
                <h3 className="font-semibold text-[#1e3a8a] mb-2">How to Use:</h3>
                <ul className="space-y-2 text-sm text-slate-700">
                  <li>• <strong>Toggle Banner:</strong> Use the switch to show/hide the banner site-wide</li>
                  <li>• <strong>Choose Template:</strong> Select from predefined messages or create custom text</li>
                  <li>• <strong>Preview:</strong> See how the banner will appear before saving</li>
                  <li>• <strong>Save Changes:</strong> Click save to update the banner for all visitors</li>
                  <li>• <strong>Note:</strong> Users cannot close this banner - only you can disable it</li>
                </ul>
              </CardContent>
            </Card>
          </div>
        )}

        {/* Client Leads Tab Content */}
        {mainTab === 'signups' && (
          <div>
            <Card>
              <CardHeader>
                <CardTitle>Client Lead Submissions ({clientSignups.length})</CardTitle>
              </CardHeader>
              <CardContent>
                {loading ? (
                  <div className="text-center py-12">
                    <p className="text-slate-600">Loading client leads...</p>
                  </div>
                ) : clientSignups.length === 0 ? (
                  <div className="text-center py-12">
                    <p className="text-slate-600">No client submissions yet</p>
                  </div>
                ) : (
                  <div className="overflow-x-auto">
                    <table className="w-full text-left border-collapse">
                      <thead>
                        <tr className="border-b-2 border-slate-200">
                          <th className="p-3 text-sm font-semibold text-slate-700">Date</th>
                          <th className="p-3 text-sm font-semibold text-slate-700">Name</th>
                          <th className="p-3 text-sm font-semibold text-slate-700">Email</th>
                          <th className="p-3 text-sm font-semibold text-slate-700">Phone</th>
                          <th className="p-3 text-sm font-semibold text-slate-700">Locations</th>
                          <th className="p-3 text-sm font-semibold text-slate-700">Home Type</th>
                          <th className="p-3 text-sm font-semibold text-slate-700">Budget</th>
                          <th className="p-3 text-sm font-semibold text-slate-700">Timeline</th>
                          <th className="p-3 text-sm font-semibold text-slate-700">Client Notes</th>
                          <th className="p-3 text-sm font-semibold text-slate-700">Admin Notes</th>
                        </tr>
                      </thead>
                      <tbody>
                        {clientSignups.map((signup, index) => (
                          <tr 
                            key={signup.id} 
                            className={`border-b border-slate-100 ${index % 2 === 0 ? 'bg-white' : 'bg-slate-50'}`}
                            data-testid="signup-row"
                          >
                            <td className="p-3 text-sm text-slate-600">
                              {new Date(signup.created_at).toLocaleDateString()}
                            </td>
                            <td className="p-3 text-sm font-medium text-slate-900">
                              {signup.full_name}
                            </td>
                            <td className="p-3 text-sm text-slate-600">
                              <a href={`mailto:${signup.email}`} className="text-blue-600 hover:underline">
                                {signup.email}
                              </a>
                            </td>
                            <td className="p-3 text-sm text-slate-600">
                              <a href={`tel:${signup.phone}`} className="text-blue-600 hover:underline">
                                {signup.phone}
                              </a>
                            </td>
                            <td className="p-3 text-sm text-slate-600">
                              {signup.preferred_locations?.join(', ') || '-'}
                            </td>
                            <td className="p-3 text-sm text-slate-600">
                              {signup.desired_home_type || '-'}
                            </td>
                            <td className="p-3 text-sm text-slate-600">
                              {signup.budget_range || '-'}
                            </td>
                            <td className="p-3 text-sm text-slate-600">
                              {signup.buying_timeline || '-'}
                            </td>
                            <td className="p-3 text-sm text-slate-600 max-w-xs">
                              {signup.notes || '-'}
                            </td>
                            <td className="p-3">
                              <div className="flex items-center space-x-2">
                                <Textarea
                                  value={editingNotes[signup.id] !== undefined ? editingNotes[signup.id] : (signup.admin_notes || '')}
                                  onChange={(e) => setEditingNotes({...editingNotes, [signup.id]: e.target.value})}
                                  placeholder="Add admin notes..."
                                  className="text-sm min-w-[200px]"
                                  rows={2}
                                  data-testid={`admin-notes-${signup.id}`}
                                />
                                <Button
                                  size="sm"
                                  onClick={() => handleUpdateNotes(signup.id, editingNotes[signup.id] || signup.admin_notes || '')}
                                  className="bg-[#1e3a8a] hover:bg-[#1e3a8a]/90 text-white"
                                  data-testid={`save-notes-${signup.id}`}
                                >
                                  Save
                                </Button>
                              </div>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </CardContent>
            </Card>
          </div>
        )}
        {/* Open Houses Tab Content */}
        {mainTab === 'openhouses' && (
          <div className="space-y-6">
            {/* Create New Open House */}
            <Card>
              <CardHeader>
                <CardTitle className="text-xl font-bold text-[#1e3a8a]">
                  Create New Open House
                </CardTitle>
              </CardHeader>
              <CardContent>
                <form onSubmit={handleCreateOpenHouse} className="space-y-4">
                  <div className="grid md:grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label htmlFor="address">Property Address *</Label>
                      <Input
                        id="address"
                        placeholder="123 Main St, City, State ZIP"
                        value={newOpenHouse.address}
                        onChange={(e) => setNewOpenHouse({...newOpenHouse, address: e.target.value})}
                        required
                        data-testid="openhouse-address-input"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="open_house_date">Date & Time *</Label>
                      <Input
                        id="open_house_date"
                        type="datetime-local"
                        value={newOpenHouse.open_house_date}
                        onChange={(e) => setNewOpenHouse({...newOpenHouse, open_house_date: e.target.value})}
                        required
                        data-testid="openhouse-date-input"
                      />
                    </div>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="description">Description</Label>
                    <Textarea
                      id="description"
                      placeholder="Describe the property, open house details, special features..."
                      value={newOpenHouse.description}
                      onChange={(e) => setNewOpenHouse({...newOpenHouse, description: e.target.value})}
                      rows={3}
                      data-testid="openhouse-description-input"
                    />
                  </div>

                  <div className="grid md:grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label htmlFor="flyer">Flyer (Optional)</Label>
                      <div className="flex items-center space-x-2">
                        <Input
                          id="flyer"
                          type="file"
                          accept=".pdf,.jpg,.jpeg,.png"
                          onChange={(e) => handleFileSelect(e, 'flyer')}
                          data-testid="openhouse-flyer-input"
                        />
                        {selectedFlyer && (
                          <Button
                            type="button"
                            size="sm"
                            variant="outline"
                            onClick={() => removeFile(0, 'flyer')}
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        )}
                      </div>
                      {selectedFlyer && (
                        <p className="text-sm text-slate-600">Selected: {selectedFlyer.name}</p>
                      )}
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor="mls">MLS Listing (Optional)</Label>
                      <div className="flex items-center space-x-2">
                        <Input
                          id="mls"
                          type="file"
                          accept=".pdf,.jpg,.jpeg,.png"
                          onChange={(e) => handleFileSelect(e, 'mls')}
                          data-testid="openhouse-mls-input"
                        />
                        {selectedMLSListing && (
                          <Button
                            type="button"
                            size="sm"
                            variant="outline"
                            onClick={() => removeFile(0, 'mls')}
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        )}
                      </div>
                      {selectedMLSListing && (
                        <p className="text-sm text-slate-600">Selected: {selectedMLSListing.name}</p>
                      )}
                    </div>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="photos">Photos (jpg, png, gif)</Label>
                    <Input
                      id="photos"
                      type="file"
                      accept=".jpg,.jpeg,.png,.gif"
                      multiple
                      onChange={(e) => handleFileSelect(e, 'photos')}
                      data-testid="openhouse-photos-input"
                    />
                    {selectedPhotos.length > 0 && (
                      <div className="flex flex-wrap gap-2 mt-2">
                        {selectedPhotos.map((photo, index) => (
                          <div key={index} className="relative bg-slate-100 p-2 rounded flex items-center space-x-2">
                            <ImageIcon className="h-4 w-4 text-slate-600" />
                            <span className="text-sm text-slate-600 max-w-[150px] truncate">{photo.name}</span>
                            <Button
                              type="button"
                              size="sm"
                              variant="ghost"
                              onClick={() => removeFile(index, 'photos')}
                              className="h-6 w-6 p-0"
                            >
                              <X className="h-4 w-4" />
                            </Button>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="videos">Videos (mp4, mov)</Label>
                    <Input
                      id="videos"
                      type="file"
                      accept=".mp4,.mov"
                      multiple
                      onChange={(e) => handleFileSelect(e, 'videos')}
                      data-testid="openhouse-videos-input"
                    />
                    {selectedVideos.length > 0 && (
                      <div className="flex flex-wrap gap-2 mt-2">
                        {selectedVideos.map((video, index) => (
                          <div key={index} className="relative bg-slate-100 p-2 rounded flex items-center space-x-2">
                            <FileText className="h-4 w-4 text-slate-600" />
                            <span className="text-sm text-slate-600 max-w-[150px] truncate">{video.name}</span>
                            <Button
                              type="button"
                              size="sm"
                              variant="ghost"
                              onClick={() => removeFile(index, 'videos')}
                              className="h-6 w-6 p-0"
                            >
                              <X className="h-4 w-4" />
                            </Button>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>

                  {uploadProgress > 0 && (
                    <div className="space-y-2">
                      <div className="flex justify-between text-sm text-slate-600">
                        <span>Upload Progress</span>
                        <span>{uploadProgress}%</span>
                      </div>
                      <div className="w-full bg-slate-200 rounded-full h-2">
                        <div
                          className="bg-[#1e3a8a] h-2 rounded-full transition-all duration-300"
                          style={{ width: `${uploadProgress}%` }}
                        />
                      </div>
                    </div>
                  )}

                  <Button
                    type="submit"
                    disabled={loading}
                    className="w-full bg-[#1e3a8a] hover:bg-[#1e3a8a]/90 text-white"
                    data-testid="create-openhouse-btn"
                  >
                    <Upload className="mr-2 h-5 w-5" />
                    {loading ? 'Creating...' : 'Create Open House'}
                  </Button>
                </form>
              </CardContent>
            </Card>

            {/* Existing Open Houses */}
            <Card>
              <CardHeader>
                <CardTitle className="text-xl font-bold text-[#1e3a8a]">
                  Existing Open Houses ({openHouses.length})
                </CardTitle>
              </CardHeader>
              <CardContent>
                {openHouses.length === 0 ? (
                  <div className="text-center py-12 text-slate-600">
                    <Home className="h-16 w-16 mx-auto mb-4 text-slate-400" />
                    <p>No open houses yet. Create your first one above!</p>
                  </div>
                ) : (
                  <div className="space-y-4">
                    {openHouses.map((house) => (
                      <Card key={house.id} className="border-l-4 border-l-[#1e3a8a]" data-testid="openhouse-card">
                        <CardContent className="pt-6">
                          {editingOpenHouse === house.id ? (
                            /* Edit Mode */
                            <form onSubmit={handleUpdateOpenHouse} className="space-y-4">
                              <div className="grid md:grid-cols-2 gap-4">
                                <div className="space-y-2">
                                  <Label htmlFor={`edit-address-${house.id}`}>Property Address *</Label>
                                  <Input
                                    id={`edit-address-${house.id}`}
                                    value={editFormData.address}
                                    onChange={(e) => setEditFormData({...editFormData, address: e.target.value})}
                                    required
                                  />
                                </div>
                                <div className="space-y-2">
                                  <Label htmlFor={`edit-date-${house.id}`}>Date & Time *</Label>
                                  <Input
                                    id={`edit-date-${house.id}`}
                                    type="datetime-local"
                                    value={editFormData.open_house_date}
                                    onChange={(e) => setEditFormData({...editFormData, open_house_date: e.target.value})}
                                    required
                                  />
                                </div>
                              </div>

                              <div className="space-y-2">
                                <Label htmlFor={`edit-description-${house.id}`}>Description</Label>
                                <Textarea
                                  id={`edit-description-${house.id}`}
                                  value={editFormData.description}
                                  onChange={(e) => setEditFormData({...editFormData, description: e.target.value})}
                                  rows={2}
                                />
                              </div>

                              <div className="grid md:grid-cols-2 gap-4">
                                <div className="space-y-2">
                                  <Label>Replace Flyer (Optional)</Label>
                                  <Input
                                    type="file"
                                    accept=".pdf,.jpg,.jpeg,.png"
                                    onChange={(e) => handleFileSelect(e, 'flyer')}
                                  />
                                  {selectedFlyer && (
                                    <p className="text-sm text-slate-600">New: {selectedFlyer.name}</p>
                                  )}
                                </div>
                                <div className="space-y-2">
                                  <Label>Replace MLS Listing (Optional)</Label>
                                  <Input
                                    type="file"
                                    accept=".pdf,.jpg,.jpeg,.png"
                                    onChange={(e) => handleFileSelect(e, 'mls')}
                                  />
                                  {selectedMLSListing && (
                                    <p className="text-sm text-slate-600">New: {selectedMLSListing.name}</p>
                                  )}
                                </div>
                              </div>

                              <div className="space-y-2">
                                <Label>Add More Photos (Optional)</Label>
                                <Input
                                  type="file"
                                  accept=".jpg,.jpeg,.png,.gif"
                                  multiple
                                  onChange={(e) => handleFileSelect(e, 'photos')}
                                />
                                {selectedPhotos.length > 0 && (
                                  <p className="text-sm text-slate-600">{selectedPhotos.length} new photo(s) to add</p>
                                )}
                              </div>

                              <div className="space-y-2">
                                <Label>Add More Videos (Optional)</Label>
                                <Input
                                  type="file"
                                  accept=".mp4,.mov"
                                  multiple
                                  onChange={(e) => handleFileSelect(e, 'videos')}
                                />
                                {selectedVideos.length > 0 && (
                                  <p className="text-sm text-slate-600">{selectedVideos.length} new video(s) to add</p>
                                )}
                              </div>

                              {uploadProgress > 0 && (
                                <div className="space-y-2">
                                  <div className="flex justify-between text-sm text-slate-600">
                                    <span>Update Progress</span>
                                    <span>{uploadProgress}%</span>
                                  </div>
                                  <div className="w-full bg-slate-200 rounded-full h-2">
                                    <div
                                      className="bg-[#1e3a8a] h-2 rounded-full transition-all duration-300"
                                      style={{ width: `${uploadProgress}%` }}
                                    />
                                  </div>
                                </div>
                              )}

                              <div className="flex space-x-2">
                                <Button
                                  type="submit"
                                  disabled={loading}
                                  className="bg-[#1e3a8a] hover:bg-[#1e3a8a]/90 text-white"
                                >
                                  <Save className="mr-2 h-4 w-4" />
                                  {loading ? 'Saving...' : 'Save Changes'}
                                </Button>
                                <Button
                                  type="button"
                                  onClick={handleCancelEdit}
                                  variant="outline"
                                  disabled={loading}
                                >
                                  Cancel
                                </Button>
                              </div>
                            </form>
                          ) : (
                            /* View Mode */
                            <div className="flex flex-col md:flex-row justify-between">
                              <div className="flex-1">
                                <div className="flex items-start justify-between mb-3">
                                  <div>
                                    <h3 className="text-lg font-semibold text-[#1e3a8a] flex items-center">
                                      <MapPin className="h-5 w-5 mr-2 text-[#d4af37]" />
                                      {house.address}
                                    </h3>
                                    <p className="text-sm text-slate-500 flex items-center mt-1">
                                      <Calendar className="h-4 w-4 mr-2" />
                                      {new Date(house.open_house_date).toLocaleString('en-US', {
                                        weekday: 'long',
                                        year: 'numeric',
                                        month: 'long',
                                        day: 'numeric',
                                        hour: 'numeric',
                                        minute: '2-digit',
                                        hour12: true
                                      })}
                                    </p>
                                  </div>
                                  <div className="flex items-center space-x-2">
                                    <span className={`px-3 py-1 rounded-full text-xs font-medium ${
                                      house.is_active
                                        ? 'bg-green-100 text-green-800'
                                        : 'bg-slate-100 text-slate-600'
                                    }`}>
                                      {house.is_active ? 'Visible' : 'Hidden'}
                                    </span>
                                  </div>
                                </div>

                                {house.description && (
                                  <p className="text-slate-600 mb-3">{house.description}</p>
                                )}

                                <div className="flex flex-wrap gap-2 mb-3">
                                  {house.flyer_url && (
                                    <span className="text-xs bg-blue-50 text-blue-700 px-2 py-1 rounded">
                                      Flyer Uploaded
                                    </span>
                                  )}
                                  {house.mls_listing_url && (
                                    <span className="text-xs bg-purple-50 text-purple-700 px-2 py-1 rounded">
                                      MLS Listing
                                    </span>
                                  )}
                                  {house.photos?.length > 0 && (
                                    <span className="text-xs bg-green-50 text-green-700 px-2 py-1 rounded">
                                      {house.photos.length} Photo{house.photos.length > 1 ? 's' : ''}
                                    </span>
                                  )}
                                  {house.videos?.length > 0 && (
                                    <span className="text-xs bg-orange-50 text-orange-700 px-2 py-1 rounded">
                                      {house.videos.length} Video{house.videos.length > 1 ? 's' : ''}
                                    </span>
                                  )}
                                </div>

                                {house.photos && house.photos.length > 0 && (
                                  <div className="flex gap-2 overflow-x-auto">
                                    {house.photos.slice(0, 4).map((photo, idx) => (
                                      <img
                                        key={idx}
                                        src={photo}
                                        alt={`${house.address} - ${idx + 1}`}
                                        className="h-20 w-20 object-cover rounded cursor-pointer hover:opacity-75"
                                        onClick={() => window.open(photo, '_blank')}
                                      />
                                    ))}
                                  </div>
                                )}
                              </div>

                              <div className="flex md:flex-col space-x-2 md:space-x-0 md:space-y-2 mt-4 md:mt-0 md:ml-4">
                                <Button
                                  size="sm"
                                  onClick={() => handleEditOpenHouse(house)}
                                  variant="outline"
                                  className="border-blue-300 text-blue-600 hover:bg-blue-50"
                                  data-testid={`edit-openhouse-${house.id}`}
                                >
                                  <Edit className="h-4 w-4 mr-1" />
                                  Edit
                                </Button>
                                <Button
                                  size="sm"
                                  onClick={() => handleToggleOpenHouse(house.id)}
                                  variant="outline"
                                  className={house.is_active ? 'border-slate-300' : 'border-green-500 text-green-600'}
                                  data-testid={`toggle-openhouse-${house.id}`}
                                >
                                  <Eye className="h-4 w-4 mr-1" />
                                  {house.is_active ? 'Hide' : 'Show'}
                                </Button>
                                <Button
                                  size="sm"
                                  onClick={() => handleDeleteOpenHouse(house.id)}
                                  variant="outline"
                                  className="border-red-300 text-red-600 hover:bg-red-50"
                                  data-testid={`delete-openhouse-${house.id}`}
                                >
                                  <Trash2 className="h-4 w-4 mr-1" />
                                  Delete
                                </Button>
                              </div>
                            </div>
                          )}
                        </CardContent>
                      </Card>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </div>
        )}

        {/* Settings Tab Content */}
        {/* Properties Tab Content */}
        {mainTab === 'properties' && (
          <div className="space-y-6">
            {/* Upload New Property */}
            <Card>
              <CardHeader>
                <CardTitle className="text-xl font-bold text-[#1e3a8a]">
                  Upload Property Brochure
                </CardTitle>
                <p className="text-sm text-slate-600 mt-2">
                  Add property brochures for different cities and builders. Users can view and download them.
                </p>
              </CardHeader>
              <CardContent>
                <form onSubmit={handlePropertyUpload} className="space-y-4">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label htmlFor="city">City *</Label>
                      <Input
                        id="city"
                        type="text"
                        placeholder="e.g., Minneola, FL"
                        value={newProperty.city}
                        onChange={(e) => setNewProperty({ ...newProperty, city: e.target.value })}
                        data-testid="property-city-input"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="builder">Builder *</Label>
                      <Input
                        id="builder"
                        type="text"
                        placeholder="e.g., Dream Finders Homes"
                        value={newProperty.builder}
                        onChange={(e) => setNewProperty({ ...newProperty, builder: e.target.value })}
                        data-testid="property-builder-input"
                      />
                    </div>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="brochure-input">Brochure (PDF) *</Label>
                    <Input
                      id="brochure-input"
                      type="file"
                      accept="application/pdf"
                      onChange={(e) => {
                        if (e.target.files && e.target.files[0]) {
                          setSelectedBrochure(e.target.files[0]);
                        }
                      }}
                      data-testid="brochure-input"
                    />
                    {selectedBrochure && (
                      <p className="text-sm text-slate-600">
                        Selected: {selectedBrochure.name}
                      </p>
                    )}
                  </div>

                  {propertyUploadProgress > 0 && (
                    <div className="space-y-2">
                      <div className="flex justify-between text-sm text-slate-600">
                        <span>Upload Progress</span>
                        <span>{propertyUploadProgress}%</span>
                      </div>
                      <div className="w-full bg-slate-200 rounded-full h-2">
                        <div
                          className="bg-[#1e3a8a] h-2 rounded-full transition-all duration-300"
                          style={{ width: `${propertyUploadProgress}%` }}
                        />
                      </div>
                    </div>
                  )}

                  <Button
                    type="submit"
                    disabled={loading || !newProperty.city || !newProperty.builder || !selectedBrochure}
                    className="bg-[#1e3a8a] hover:bg-[#1e3a8a]/90 text-white"
                    data-testid="upload-property-btn"
                  >
                    <Upload className="mr-2 h-5 w-5" />
                    {loading ? 'Uploading...' : 'Upload Property'}
                  </Button>
                </form>
              </CardContent>
            </Card>

            {/* Properties List */}
            <Card>
              <CardHeader>
                <CardTitle className="text-xl font-bold text-[#1e3a8a]">
                  Manage Properties ({properties.length})
                </CardTitle>
              </CardHeader>
              <CardContent>
                {properties.length === 0 ? (
                  <div className="text-center py-8 text-slate-600">
                    <Building2 className="h-12 w-12 text-slate-300 mx-auto mb-3" />
                    <p>No properties uploaded yet</p>
                  </div>
                ) : (
                  <div className="space-y-4">
                    {properties.map((property) => (
                      <div
                        key={property.id}
                        className="flex items-center justify-between p-4 border border-slate-200 rounded-lg hover:bg-slate-50 transition-colors"
                      >
                        <div className="flex-1">
                          <div className="flex items-center gap-3">
                            <MapPin className="h-5 w-5 text-[#1e3a8a]" />
                            <div>
                              <h4 className="font-semibold text-slate-900">{property.city}</h4>
                              <p className="text-sm text-slate-600 flex items-center gap-2 mt-1">
                                <Building2 className="h-4 w-4" />
                                {property.builder}
                              </p>
                            </div>
                          </div>
                        </div>
                        <div className="flex items-center gap-2">
                          <Button
                            onClick={() => window.open(property.brochure_url, '_blank')}
                            variant="outline"
                            size="sm"
                            className="border-[#1e3a8a] text-[#1e3a8a] hover:bg-[#1e3a8a] hover:text-white"
                          >
                            <Eye className="mr-2 h-4 w-4" />
                            View
                          </Button>
                          <Button
                            onClick={() => handleDeleteProperty(property.id)}
                            variant="outline"
                            size="sm"
                            className="border-red-500 text-red-500 hover:bg-red-500 hover:text-white"
                            data-testid={`delete-property-${property.id}`}
                          >
                            <Trash2 className="mr-2 h-4 w-4" />
                            Delete
                          </Button>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </div>
        )}


        {/* Analytics Tab Content */}
        {mainTab === 'analytics' && (
          <AnalyticsView />
        )}


        {mainTab === 'settings' && (
          <div className="space-y-6">
            {/* Agent Photo Upload */}
            <Card>
              <CardHeader>
                <CardTitle className="text-xl font-bold text-[#1e3a8a]">
                  Upload Your Professional Photo
                </CardTitle>
                <p className="text-sm text-slate-600 mt-2">
                  This photo will be displayed on the Home page and About page.
                </p>
              </CardHeader>
              <CardContent>
                <form onSubmit={handleAgentPhotoUpload} className="space-y-4">
                  {/* Current Photo Preview */}
                  {agentPhotoUrl && (
                    <div className="space-y-2">
                      <Label>Current Photo</Label>
                      <div className="flex items-center space-x-4">
                        <img
                          src={agentPhotoUrl}
                          alt="Agent Profile"
                          className="w-32 h-32 rounded-full object-cover border-4 border-[#1e3a8a]"
                        />
                        <div className="text-sm text-slate-600">
                          <p>✅ Photo uploaded</p>
                          <p className="text-xs mt-1">Upload a new photo below to replace it</p>
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Upload New Photo */}
                  <div className="space-y-2">
                    <Label htmlFor="agent-photo-input">
                      {agentPhotoUrl ? 'Replace Photo' : 'Upload Photo'} *
                    </Label>
                    <Input
                      id="agent-photo-input"
                      type="file"
                      accept="image/*"
                      onChange={(e) => {
                        if (e.target.files && e.target.files[0]) {
                          setAgentPhoto(e.target.files[0]);
                        }
                      }}
                      data-testid="agent-photo-input"
                    />
                    {agentPhoto && (
                      <p className="text-sm text-slate-600">
                        Selected: {agentPhoto.name}
                      </p>
                    )}
                    <p className="text-xs text-slate-500">
                      Recommended: Square image, at least 400x400px, JPG or PNG format
                    </p>
                  </div>

                  {photoUploadProgress > 0 && (
                    <div className="space-y-2">
                      <div className="flex justify-between text-sm text-slate-600">
                        <span>Upload Progress</span>
                        <span>{photoUploadProgress}%</span>
                      </div>
                      <div className="w-full bg-slate-200 rounded-full h-2">
                        <div
                          className="bg-[#1e3a8a] h-2 rounded-full transition-all duration-300"
                          style={{ width: `${photoUploadProgress}%` }}
                        />
                      </div>
                    </div>
                  )}

                  <Button
                    type="submit"
                    disabled={loading || !agentPhoto}
                    className="bg-[#1e3a8a] hover:bg-[#1e3a8a]/90 text-white"
                    data-testid="upload-agent-photo-btn"
                  >
                    <Upload className="mr-2 h-5 w-5" />
                    {loading ? 'Uploading...' : agentPhotoUrl ? 'Replace Photo' : 'Upload Photo'}
                  </Button>
                </form>
              </CardContent>
            </Card>
          </div>
        )}
      </div>
    </div>
  );
}
